
import FileSaver from 'file-saver'
import '@/assets/css/remoteDataTable.css'
import { debounce, isEqual } from 'lodash'

/**
 * Defines common properties and methods for tables fetched from the API for a particular model.
 * This is a reworked version for Vuetify from the old Bootstrap Vue version.
 * This version is somewhat more client-side driven than the Bootstrap Vue version
 */
const RemoteDataTableMixin = {
  data () {
    return {
      remoteData: {
        items: [],
        isBusy: false,
        isLoaded: false,
        isDownloading: false,
        currentPage: 1,
        rowsPerPage: 20,
        totalRows: 0,
        sortBy: null,
        sortDesc: false,
        pageOptions: [10, 20, 50, 100]
      },
      loadRemoteDataDelayed: debounce(this.loadRemoteData, 500)
    }
  },
  computed: {
    /**
     * Returns a flag that indicates if the search criteria and table navigation (paging/sorting) match the default
     * values
     *
     * @return Boolean
     */
    tableControlsMatchDefaults () {
      return isEqual(this.searchCriteria, this.getDefaultSearchCriteria()) &&
        this.remoteData.currentPage === 1 &&
        this.remoteData.sortBy === null &&
        this.remoteData.sortDesc === false
    }
  },
  methods: {
    /**
     * Sets our data table back to its original state before the user-applied filtering and paging changes
     *
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.initiateSearch()
    },

    /**
     * Reset the paging and sorting properties on this component
     *
     * @return undefined
     */
    resetRemoteDataTableNavigation () {
      this.remoteData.currentPage = 1
      this.remoteData.sortBy = null
      this.remoteData.sortDesc = false
    },

    /**
     * Load data from the API based on the current search criteria.
     * @return Array|null
     */
    async loadRemoteData () {
      this.remoteData.isBusy = true
      try {
        const searchCriteria = this.buildSearchCriteria()
        const collection = await this.$orm(this.model).findAll(searchCriteria)
        this.remoteData.currentPage = collection.getCurrentPage()
        this.remoteData.totalRows = collection.getTotalItemCount()
        this.remoteData.items = this.transformRemoteData(collection.getItems())
      } catch (error) {
        await this.$store.dispatch('globalMessagingModule/showModal', {
          message: this.$t('network_error_message')
        })
        throw (error)
      } finally {
        this.remoteData.isBusy = false
        this.remoteData.isLoaded = true
      }
    },

    /**
     * Wrapper to invoke debounced version of loadRemoteData
     */
    refreshResults () {
      this.remoteData.isBusy = true
      this.loadRemoteDataDelayed()
    },

    /**
     * Download a csv report of the current search criteria
     *
     * @return null
     */
    async downloadRemoteDataCsv () {
      // Update the UI to show the spinner on the download button
      this.remoteData.isDownloading = true

      // Fire an analytics event
      this.$gtag.event('download', { event_category: this.model })

      const searchCriteria = this.buildSearchCriteria()
      // Omit the parameters that are not relevant to this request.
      delete searchCriteria.expand
      delete searchCriteria.fields
      delete searchCriteria.page
      delete searchCriteria['per-page']

      const request = {
        url: this.$orm(this.model).endpoint,
        method: 'GET',
        headers: {
          Accept: 'text/csv'
        },
        params: searchCriteria,
        responseType: 'blob' // important
      }

      try {
        const response = await this.$httpAdapter(request)
        // Parse the file name from the content-disposition header
        const headerLine = response.headers['content-disposition']
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex)
        FileSaver.saveAs(new Blob([response.data]), fileName)
      } catch (error) {
        if (error) {
          await this.$store.dispatch('globalMessagingModule/showModal', {
            message: this.$t('network_error_message')
          })
          throw (error)
        }
      } finally {
        this.remoteData.isDownloading = false
      }
    },

    /**
     * Initiate a new search. The assumption here is that the user has initiated a new search by changing the filters,
     * sorting, or rows per page, and we want to reset the display back to page 1.
     *
     * By contrast, when the user merely paginates on an existing search, refreshResults should be directly called
     * instead.
     *
     * @return undefined
     */
    initiateSearch () {
      // We are not paginating, so this should be considered a new search, and we reset the page to 1.
      this.remoteData.currentPage = 1
      if (this.model != null) {
        this.$gtag.event('search', { event_category: this.model })
      }
      this.refreshResults()
    },

    /**
     * When customer context updates, refresh the table
     */
    customerContextChangeHandler () {
      this.initiateSearch()
    },

    /**
     * Overwrite this function if you wish to transform the results of the remote load
     *
     * @param {Array} items
     */
    transformRemoteData (items) {
      return items
    }
  }
}

export default RemoteDataTableMixin
